
import React, { useEffect, useState, useMemo } from 'react';
import {
  Table,
  TableBody,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useTranslation } from 'react-i18next';
import { usePractitionerAppointmentHistory } from '../hooks/usePractitionerAppointmentHistory';
import { PractitionerAppointmentTableRow } from './PractitionerAppointmentTableRow';
import { useUnreadMessages } from '@/hooks/useUnreadMessages';
import { MarkAllAsReadButton } from '../../shared/MarkAllAsReadButton';
import { RefreshCcw } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';

interface PractitionerAppointmentHistoryProps {
  unreadByBooking?: Record<string, number>;
  appointmentId?: string | null;
  onUnreadCountChange?: (count: number) => void;
}

export const PractitionerAppointmentHistory: React.FC<PractitionerAppointmentHistoryProps> = ({
  unreadByBooking = {},
  onUnreadCountChange
}) => {
  const { t } = useTranslation('dashboard');
  const { user } = useAuth();
  const [practitionerTimezone, setPractitionerTimezone] = useState<string>('America/New_York');
  const { 
    appointments, 
    isLoading, 
    selectedNotes, 
    setSelectedNotes, 
    refreshAppointments 
  } = usePractitionerAppointmentHistory();
  const { unreadCounts, refreshUnreadCounts } = useUnreadMessages({ 
    userId: undefined,
    userType: 'practitioner'
  });

  useEffect(() => {
    const fetchTimezone = async () => {
      if (!user?.id) return;
      const { data } = await supabase.from('practitioners').select('timezone').eq('id', user.id).single();
      if (data?.timezone) setPractitionerTimezone(data.timezone);
    };
    fetchTimezone();
  }, [user?.id]);

  useEffect(() => {
    refreshUnreadCounts();
  }, [refreshUnreadCounts]);

  // Calculate total unread count for history appointments only
  const historyUnreadCount = useMemo(() => {
    const total = appointments.reduce((sum, appointment) => {
      return sum + (unreadByBooking[appointment.id] || 0);
    }, 0);

    return total;
  }, [appointments, unreadByBooking]);

  // Notify parent component when unread count changes
  useEffect(() => {
    if (onUnreadCountChange) {
      onUnreadCountChange(historyUnreadCount);
    }
  }, [historyUnreadCount, onUnreadCountChange]);

  const handleMarkAllAsRead = async (): Promise<boolean> => {
    console.log('🔄 PractitionerAppointmentHistory: Attempting to mark all history messages as read');
    
    // Mark all appointments as read
    const appointmentIds = appointments.map(app => app.id);
    for (const appointmentId of appointmentIds) {
      // Mark messages for each booking/appointment
      // This is a simplified approach since we don't have the markAllMessagesAsRead function
    }
    
    // Force refresh the unread counts and appointment data
    setTimeout(() => {
      console.log('🔄 PractitionerAppointmentHistory: Forcing refresh after mark all');
      refreshUnreadCounts();
      refreshAppointments();
    }, 1000);
    
    return true;
  };

  if (isLoading) {
    return <div className="text-center py-4">{t('appointments.history.loading')}</div>;
  }

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between gap-2 min-w-0">
            <CardTitle className="flex items-center truncate">
              {t('appointments.history.title')}
              {unreadCounts.totalUnread > 0 && (
                <span className="ml-2 bg-red-500 text-white text-xs rounded-full h-5 px-2 min-w-5 flex items-center justify-center">
                  {unreadCounts.totalUnread}
                </span>
              )}
            </CardTitle>
            <div className="flex items-center gap-2 shrink-0">
              <MarkAllAsReadButton 
                onClick={handleMarkAllAsRead}
                unreadCount={unreadCounts.totalUnread}
                isLoading={false}
                section="history"
              />
              <Button 
                variant="outline" 
                onClick={() => {
                  refreshAppointments();
                  refreshUnreadCounts();
                }}
                size="sm"
                className="h-8 w-8 shrink-0 sm:h-9 sm:w-auto sm:px-4"
                aria-label={t('appointments.actions.refresh')}
                title={t('appointments.actions.refresh')}
              >
                <RefreshCcw className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">{t('appointments.actions.refresh')}</span>
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {appointments.length === 0 ? (
            <p className="text-gray-500 text-center py-4">{t('appointments.history.noAppointments')}</p>
          ) : (
            <div className="rounded-md border overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('appointments.table.date')}</TableHead>
                    <TableHead>{t('appointments.table.time')}</TableHead>
                    <TableHead>{t('appointments.table.client')}</TableHead>
                    <TableHead>{t('appointments.table.service')}</TableHead>
                    <TableHead>{t('appointments.table.status')}</TableHead>
                    <TableHead>{t('appointments.table.format')}</TableHead>
                    <TableHead>{t('appointments.table.actions')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {appointments.map((appointment) => (
                    <PractitionerAppointmentTableRow
                      key={appointment.id}
                      appointment={appointment}
                      onNotesClick={setSelectedNotes}
                      onRefresh={refreshAppointments}
                      unreadCount={unreadCounts.unreadByBooking[appointment.id] || 0}
                      practitionerTimezone={practitionerTimezone}
                    />
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Notes Modal */}
      {selectedNotes && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg max-w-md w-full mx-4">
            <h3 className="text-lg font-medium mb-4">{t('appointments.notes.title')}</h3>
            <p className="text-gray-700 mb-4">{selectedNotes}</p>
            <Button onClick={() => setSelectedNotes(null)}>{t('appointments.actions.close')}</Button>
          </div>
        </div>
      )}
    </>
  );
};
